import { clone } from 'lodash';

export default [ 'basket', 'propertyService', 'messageService', 'guestnameService', 'storeService', (basket, propertyService, messageService, guestnameService, storeService) => {
  let state = {
    existingGuests: null,
    gender: null,
    guests: [],
    maximumGuests: null,
    minimumGuests: null,
    staff: null,
    titles: []
  };

  const setState = (stateToSet, param = null) => {
    if (param) {
      state[param] = stateToSet;
      return;
    }

    state = Object.assign({}, state, stateToSet);
  };

  const getState = (param = null) => {
    if (param) {
      return state[param];
    }

    return state;
  };

  const getGuests = () => {
    return state.guests;
  };

  const buildTitlesList = () => {
    let male = [ 'Mr.' ];
    let female = [ 'Miss', 'Mrs.', 'Ms.' ];
    let output = [ 'Dr.', 'Rev.' ];

    switch (state.gender) {
    case 'M':
      output = male.concat(output);
      break;

    case 'F':
      output = female.concat(output);
      break;

    default:
      output = female.concat(male)
        .concat(output);
      break;
    }

    state.titles = output;
    return output;
  };

  const setInitialGuests = (guests = []) => {
    state.guests = guests;

    if (state.guests.length) {
      state.guests = filterGuestsByGender(state.guests, true);
    }

    if (state.guests.length < state.minimumGuests && notEditing()) {
      addEmptyGuests(state.guests.length);
      return state.guests;
    }

    if (state.guests.length > state.maximumGuests) {
      state.guests.splice(state.maximumGuests);
    }

    return state.guests;
  };

  const canAddGuest = () => {
    return (parseInt(state.guests.length) + parseInt(state.minimumGuests)) <= parseInt(state.maximumGuests);
  };

  const addEmptyGuests = (startingIndex = 0, reference = null) => {
    if (!reference) {
      reference = basket.referenceGenerator();
    }

    for (let index = startingIndex; index < state.minimumGuests; index++) {
      pushGuest(reference);
    }
  };

  const pushGuest = (reference) => {
    let attributes = {
      reference: reference,
      item_children: []
    };

    if (state.gender) {
      attributes.gender = state.gender;
    }

    if (state.staff && state.staff.length === 1) {
      attributes.staff = state.staff[0];
    }

    state.guests.push(attributes);
  };

  const hasMaxGuests = () => {
    return parseInt(state.guests.length) === parseInt(state.maximumGuests);
  };

  const maxGuestsAdded = () => {
    return hasMaxGuests() && firstEmptyGuest() === -1;
  };

  const firstEmptyGuest = () => {
    return state.guests.findIndex(guest => !guest.title && !guest.first_name && !guest.last_name);
  };

  const filterGuestsByGender = (guests, ignoreTitle = false) => {
    return guests.filter(guest => {
      if (state.gender && guest.gender) {
        return state.gender.toLowerCase() === guest.gender.toLowerCase();
      }

      if (guest.integration_reference) {
        return !!state.existingGuests.find(existing => guest.integration_reference === existing.integration_reference);
      }

      if (ignoreTitle) {
        return true;
      }

      return state.titles.indexOf(guest.title) > -1;
    });
  };

  const filterExistingGuests = () => {
    setInitialGuests(getGuests());

    state.existingGuests.forEach(guest => {
      if (!guest.disabled) {
        if (state.gender
                    && guest.gender
                    && state.gender.toLowerCase() !== guest.gender.toLowerCase()) {
          guest.disabled = true;
        }

        return;
      }

      if (guest.disabled
                && !state.guests.find(newGuest => guest.id && guest.id === newGuest.id)) {
        if (!state.gender
                    || (
                      state.gender
                        && guest.gender
                        && state.gender.toLowerCase() === guest.gender.toLowerCase())
        ) {
          delete guest.disabled;
        }
      }
    });

    return state.existingGuests;
  };

  const addExistingGuest = (existingGuestId) => {
    if (maxGuestsAdded()) {
      return;
    }

    let existingGuest = {};

    if (existingGuestId) {
      existingGuest = state.existingGuests.find(guest => guest.id === existingGuestId);
      existingGuest.disabled = true;

      if (!state.guests.find(guest => guest.id === existingGuest.id)) {
        let emptyGuest = firstEmptyGuest();
        let cloneGuest = clone(existingGuest);
        cloneGuest.item_children = [];

        if (state.guests[emptyGuest] && state.guests[emptyGuest].staff) {
          delete (state.guests[emptyGuest].staff.disabled);
        }

        if (state.staff && state.staff.length === 1) {
          cloneGuest.staff = state.staff[0];
        }

        if (emptyGuest !== -1) {
          cloneGuest.reference = state.guests[emptyGuest].reference;
          state.guests[emptyGuest] = cloneGuest;
          return;
        }

        let reference = basket.referenceGenerator();
        cloneGuest.reference = reference;

        state.guests.push(cloneGuest);

        if (state.minimumGuests > 1) {
          addEmptyGuests(1, reference);
        }
      }
    }
  };

  const removeGuest = (selectedGuest) => {
    const reference = selectedGuest.reference;

    for (let index = state.guests.length - 1; index >= 0; index--) {
      let guest = state.guests[index];

      if (guest.reference === reference) {
        if (guest.staff) {
          delete guest.staff.disabled;
        }

        let existingGuest = guest.id && state.existingGuests.find(existingGuest => existingGuest.id === guest.id);
        if (existingGuest) {
          delete existingGuest.disabled;
        }

        state.guests.splice(index, 1);
      }
    }
  };

  const guestNameOrPlaceholder = (guest) => {
    if (guest.title || guest.first_name || guest.last_name) {
      return guestnameService.format(guest);
    }

    return 'New Guest';
  };

  const showTitle = (guest) => {
    if (!guest.id) {
      return true;
    }

    return state.titles.indexOf(guest.title) !== -1;
  };

  const showGenderSelection = (guest) => {
    return propertyService.get('hasIntegration') && (!state.gender && !guest.id);
  };

  const guessGender = (guest) => {
    let female = [ 'Miss', 'Mrs.', 'Ms.' ];

    if (guest.title) {
      if (guest.title === 'Mr.') {
        guest.gender = 'M';
        return;
      }

      if (female.find(title => title === guest.title)) {
        guest.gender = 'F';
      }
    }
  };

  const digitToWord = (digit) => {
    switch (parseInt(digit)) {
    case 1:
      return 'one';
    case 2:
      return 'two';
    case 3:
      return 'three';
    case 4:
      return 'four';
    case 5:
      return 'five';
    case 6:
      return 'six';
    case 7:
      return 'seven';
    case 8:
      return 'eight';
    case 9:
      return 'nine';
    }

    return digit;
  };

  const displayGuestQuantity = (type, minGuests, quantity, eventGender = null, maxGuests = null) => {
    if (!!type && quantity > 0) {
      let string = `This ${type} can be booked for ${numberOfGuests(minGuests, quantity, maxGuests)}.`;

      if (eventGender && eventGender !== 'false') {
        const gender = (eventGender === 'M') ? 'male' : 'female';
        string += ` It is only available to ${gender} guests.`;
      }

      return string;
    }
  };

  const numberOfGuests = (minGuests, quantity, maxGuests) => {
    if (!propertyService.get('hasIntegration') && !!maxGuests) {
      quantity = maxGuests;
    }

    if (parseInt(minGuests) !== parseInt(quantity)) {
      return `${minGuests} to ${quantity} guests`;
    }

    if (quantity > 1) {
      return `${quantity} guests`;
    }

    return `${quantity} guest`;
  };

  const notEditing = () => {
    return !storeService.get('reservation');
  };

  return {
    setState,
    getState,
    getGuests,
    buildTitlesList,
    setInitialGuests,
    canAddGuest,
    addEmptyGuests,
    maxGuestsAdded,
    filterGuestsByGender,
    filterExistingGuests,
    addExistingGuest,
    removeGuest,
    guestNameOrPlaceholder,
    showTitle,
    showGenderSelection,
    guessGender,
    digitToWord,
    displayGuestQuantity
  };
} ];



// WEBPACK FOOTER //
// ./frontend/admin/app/modules/shared/guest/services/guest.add.service.js